import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import orderAPI from "../../api/orderApi";
import {
  formatDate,
  getOrderStatusBadge,
  getPaymentStatusBadge,
} from "../../helper/Helper";
import { showError, showSuccess } from "../../utils/toastUtils";
import ShipmentDetail from "./ShipmentDetail";
import { UserContext } from "../../utils/UserContext";
import { Download, Printer, Truck } from "lucide-react";
import shippingAPI from "../../api/shippingApi";
import LoadingScreen from "../../components/partials/global/LoadingScreen";
import ORDER_CONSTANTS from "../../constants/orderConstants";

function Detail() {
  const { user } = useContext(UserContext);

  const { id } = useParams();
  const [order, setOrder] = useState({});
  const [newStatus, setNewStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingLogs, setTrackingLogs] = useState([]);
  const [selectedCustomization, setSelectedCustomization] = useState({});
  const [showCustomModal, setShowCustomModal] = useState(false);

  useEffect(() => {
    fetchOrderDetail();
  }, [id]);

  const fetchOrderDetail = async () => {
    setLoading(true);

    try {
      const res = await orderAPI.details({ orderId: id });
      if (res.code === 200) {
        setOrder(res.data);

        if (res.data?.statusHistoryAwb?.shipmentLogId) {
          fetchTrackingLogs(res.data?.statusHistoryAwb?.shipmentLogId);
        }
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackingLogs = async (shipmentId) => {
    try {
      const res = await shippingAPI.trackShipment(shipmentId);

      if (res.code === 200) {
        const tracking = res.data?.tracking;
        setTrackingLogs(tracking?.timeline || []);
        setTrackingData(tracking);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const updateOrders = async (status) => {
    if (!status) {
      alert("Please select a status to update.");
      return;
    }

    const orderIds = [order.id];

    const payload = {
      orders: orderIds,
      status,
    };

    const res = await orderAPI.updateStatus(payload);
    if (res.code === 200) {
      showSuccess(res.message);

      fetchOrderDetail();
    } else {
      showError(res.message);
    }
  };

  const getSecureUrl = (url) => {
    if (!url) return "";
    return `https://${url.replace(/^https?:\/\//, "")}`;
  };

  if (loading) {
    return <LoadingScreen text="Loading Order Details" />;
  }

  const shipment = order.statusHistoryAwb || {};

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div>
            <Link to="/orders/all">
              <i className="ti ti-chevron-left me-2" /> Back to List
            </Link>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-4">
            <div className="d-flex gap-2 align-items-center">
              <select
                className="form-select w-50"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                style={{ width: "200px" }}
              >
                <option value="">-- Choose Status --</option>
                {Object.entries(ORDER_CONSTANTS).map(
                  ([key, label]) =>
                    key.status !== "processing" && (
                      <option value={key} key={key}>
                        Mark as {label}
                      </option>
                    ),
                )}
              </select>

              <button
                className="btn btn-primary w-50"
                onClick={() => updateOrders(newStatus)}
              >
                Update Order Status
              </button>
            </div>
            <div className="mt-3 card rounded-0 border-0">
              <div className="card-header border-0 rounded-0 d-flex align-items-center">
                <h6>Customer Information</h6>
              </div>
              <div className="card-body pb-2">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    Name
                  </span>
                  <p className="text-dark">
                    {order?.user?.firstName} {order?.user?.lastName}
                  </p>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    Email
                  </span>
                  <p className="text-dark">
                    <a href={`mailto: ${order?.user?.email}`}>
                      {order?.user?.email}
                    </a>
                  </p>
                </div>
                <div className="d-flex align-items-center justify-content-between">
                  <span className="d-inline-flex align-items-center fw-medium">
                    Phone
                  </span>
                  <p className="text-dark">
                    <a href={`tel: ${order?.user?.mobile}`}>
                      {order?.user?.mobile}
                    </a>
                  </p>
                </div>
                {order?.gstin && (
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      GSTIN
                    </span>
                    <p className="text-dark">
                      <a href={`tel: ${order?.gstin}`}>{order?.gstin}</a>
                    </p>
                  </div>
                )}

                {order?.specialInstruction && (
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      Phone
                    </span>
                    <p className="text-dark">
                      <a href={`tel: ${order?.specialInstruction}`}>
                        {order?.specialInstruction}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="card rounded-0 border-0">
              <div className="card-header border-0 rounded-0 d-flex align-items-center">
                <h6>Customer Order's Address</h6>
              </div>
              <div className="card-body pb-2">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    Name
                  </span>
                  <p className="text-dark">
                    {order?.address?.firstName} {order?.address?.lastName}
                  </p>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    Address Type
                  </span>
                  <p className="text-dark">{order?.address?.addressType}</p>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    Address Line 1
                  </span>
                  <p className="text-dark text-end">
                    {order?.address?.addressLine1}
                  </p>
                </div>
                {order?.address?.addressLine2 && (
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      Address Line 2
                    </span>
                    <p className="text-dark">{order?.address?.addressLine2}</p>
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    District
                  </span>
                  <p className="text-dark">{order?.address?.district}</p>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    State
                  </span>
                  <p className="text-dark">{order?.address?.state}</p>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    PinCode
                  </span>
                  <p className="text-dark">{order?.address?.pinCode}</p>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="d-inline-flex align-items-center fw-medium">
                    Country
                  </span>
                  <p className="text-dark">{order?.address?.country}</p>
                </div>
              </div>
            </div>

            {order?.isShippingSameasBilling === false && order?.shippingAddress?.mobileNumber && (
              <div className="card rounded-0 border-0">
                <div className="card-header border-0 rounded-0 d-flex align-items-center">
                  <h6>Customer Shipping Address</h6>
                </div>
                <div className="card-body pb-2">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      Name
                    </span>
                    <p className="text-dark">
                      {order?.shippingAddress?.firstName}
                      {order?.shippingAddress?.lastName}
                    </p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      Mobile Number
                    </span>
                    <p className="text-dark">
                      {order?.shippingAddress?.mobileNumber}
                    </p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      Address Type
                    </span>
                    <p className="text-dark">
                      {order?.shippingAddress?.addressType}
                    </p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      Address Line 1
                    </span>
                    <p className="text-dark text-end">
                      {order?.shippingAddress?.addressLine1}
                    </p>
                  </div>
                  {order?.shippingAddress?.addressLine2 && (
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="d-inline-flex align-items-center fw-medium">
                        Address Line 2
                      </span>
                      <p className="text-dark">
                        {order?.shippingAddress?.addressLine2}
                      </p>
                    </div>
                  )}

                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      District
                    </span>
                    <p className="text-dark">
                      {order?.shippingAddress?.district}
                    </p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      State
                    </span>
                    <p className="text-dark">{order?.shippingAddress?.state}</p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      PinCode
                    </span>
                    <p className="text-dark">
                      {order?.shippingAddress?.pinCode}
                    </p>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="d-inline-flex align-items-center fw-medium">
                      Country
                    </span>
                    <p className="text-dark">
                      {order?.shippingAddress?.country}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {trackingLogs && trackingLogs?.length > 0 && (
              <div className="card rounded-0 border-0">
                <div className="card-header border-0 rounded-0 d-flex align-items-center">
                  <h6>
                    Shipment History{" "}
                    <span className="badge bg-success">
                      {trackingData.status}
                    </span>
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="shipment-history">
                        <div className="timeline">
                          {trackingLogs.map((item, index) => (
                            <div className="timeline-item" key={index}>
                              <div className="timeline-dot"></div>
                              <div className="timeline-content card p-3 mb-3">
                                <div className="d-flex justify-content-between">
                                  <h6>{item.status}</h6>
                                  <small>
                                    {formatDate(
                                      item.changedAt || item.timestamp,
                                    )}
                                  </small>
                                </div>

                                <p className="mb-1">{item.note}</p>

                                {item.location && (
                                  <small className="text-muted">
                                    📍 {item.location}
                                  </small>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="col-xl-8">
            <div className="row">
              <div className="col-md-12 d-flex">
                <Link
                  className="btn w-100 btn-outline-primary mb-3 me-2"
                  to={`/orders/invoice/${order.orderId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download size={15} className="me-2" />
                  Download Invoice
                </Link>

                {shipment?.awb && (
                  <Link
                    className="btn w-100 btn-outline-primary mb-3 me-2"
                    to={`/orders/label/${order.orderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Printer size={15} className="me-2" />
                    Print Shipment Label
                  </Link>
                )}

                <button
                  className="btn w-100 btn-outline-primary mb-3"
                  type="button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasShipment"
                  aria-controls="offcanvasShipment"
                  onClick={() => setSelectedOrder(order)}
                >
                  <Truck size={15} className="me-2" />
                  Shipment Details
                </button>
              </div>
            </div>

            <div className="card rounded-0 border-0">
              <div className="card-header border-0 rounded-0 d-flex align-items-center">
                <h6>Order Information</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <p className="fs-13 mb-0">Order ID</p>
                      <span className="text-gray-900 fs-13 fw-medium">
                        #{order?.orderId}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <p className="fs-13 mb-0">Order Date</p>
                      <span className="text-gray-900 fs-13 fw-medium">
                        {formatDate(order?.orderDate)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <p className="fs-13 mb-0">Order Status</p>
                      <span className="text-gray-900 fs-13">
                        <span
                          className={`badge ${getOrderStatusBadge(
                            order?.status,
                          )}`}
                        >
                          {order?.status}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <p className="fs-13 mb-0">Payment Status</p>
                      <span className="text-gray-900 fs-13">
                        <span
                          className={`badge ${getPaymentStatusBadge(
                            order?.payment?.status,
                          )}`}
                        >
                          {order?.payment?.status}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <p className="fs-13 mb-0">Total Amount</p>
                      <span className="text-gray-900 fs-13 fw-medium">
                        ₹{Number(order?.total || 0).toLocaleString("en-IN")}/-
                      </span>
                    </div>
                  </div>
                </div>

                {order?.payment?.method === "PARTIAL" && (
                  <div className="row mt-2">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <p className="fs-13 mb-0 text-muted">Advance Paid</p>
                        <span className="text-success fs-13 fw-bold">
                          ₹
                          {Number(
                            order?.payment?.partialPayment?.advanceAmount || 0,
                          ).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <p className="fs-13 mb-0 text-muted">
                          Balance Due (COD / Pending)
                        </p>
                        <span className="text-danger fs-13 fw-bold">
                          ₹
                          {Number(
                            order?.payment?.partialPayment?.balanceAmount || 0,
                          ).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <p className="fs-13 mb-0 text-muted">
                          Advance Transaction ID
                        </p>
                        <span className="text-gray-900 fs-13 fw-medium">
                          {order?.payment?.partialPayment
                            ?.advanceTransactionId || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {order?.items && (
              <div className="card rounded-0 border-0">
                <div className="card-header border-0 rounded-0 d-flex align-items-center">
                  <h6>Item's Information</h6>
                </div>
                <div className="table-responsive">
                  <table className="table">
                    <thead className="thead-light">
                      <tr>
                        <th>Item</th>
                        <th className="text-end">MRP</th>
                        <th className="text-end">Price</th>
                        <th className="text-end">Qty</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, index) => (
                        <tr key={item._id}>
                          <td>
                            <div className="d-flex align-items-center justify-content-between">
                              <div className="d-flex align-items-center">
                                {item.thumbnail && (
                                  <a
                                    href={item.thumbnail}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="avatar avatar-lg"
                                  >
                                    <img src={item.thumbnail} alt="img" />
                                  </a>
                                )}
                                <div className="ms-2">
                                  <h6 className="fw-bold mb-1">
                                    <a
                                      href={`${getSecureUrl(user.websiteUrl)}/shop/${item.slug}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {item.productName}
                                    </a>
                                  </h6>

                                  <div className="d-flex align-items-center flex-wrap gap-2">
                                    {item?.variantName && (
                                      <p className="fs-13 mb-0 text-muted">
                                        - {item?.variantName}
                                      </p>
                                    )}

                                    {item?.customization &&
                                      item?.customization.length > 0 && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary py-0 px-2 fs-12"
                                          onClick={() => {
                                            setSelectedCustomization(
                                              item?.customization,
                                            );
                                            setShowCustomModal(true);
                                          }}
                                        >
                                          <i className="bi bi-gear-fill me-1"></i>{" "}
                                          View Customization
                                        </button>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="text-gray-9 fw-medium text-end">
                            ₹{item.mrp}/-
                          </td>
                          <td className="text-gray-9 fw-medium text-end">
                            ₹{item.price}/-
                          </td>
                          <td className="text-gray-9 fw-medium text-end">
                            {item.quantity}
                          </td>
                          <td className="text-gray-9 fw-medium text-end">
                            ₹{item.quantity * item.price}/-
                          </td>
                        </tr>
                      ))}

                      {[
                        ["Subtotal", order.subtotal],
                        ...(order.discount > 0
                          ? [
                              [
                                "Discount",
                                `-₹${Number(order.discount).toLocaleString("en-IN")}`,
                              ],
                            ]
                          : []),
                        ...(order.shipping > 0
                          ? [["Shipping", order.shipping]]
                          : []),
                        ...(order.couponCode
                          ? [["Coupon Code", order.couponCode]]
                          : []),
                        ["Total Amount", order.total],

                        ...(order?.payment?.method === "PARTIAL" ||
                        order?.partialPayment
                          ? [
                              [
                                "Advance Paid",
                                `-₹${Number(order?.payment?.partialPayment?.advanceAmount || 0).toLocaleString("en-IN")}`,
                              ],
                              [
                                "Balance Amount (Payable)",
                                order?.payment?.partialPayment?.balanceAmount,
                              ],
                            ]
                          : []),
                      ].map(([label, value]) => (
                        <tr
                          key={label}
                          className={
                            label === "Balance Amount (Payable)"
                              ? "fw-bold text-danger"
                              : ""
                          }
                        >
                          <td></td>
                          <td
                            className="text-gray-9 fw-medium text-end"
                            colSpan={3}
                          >
                            {label}
                          </td>
                          <td className="text-gray-9 fw-medium text-end">
                            {label === "Coupon Code"
                              ? value
                              : typeof value === "string"
                                ? value
                                : `₹${Number(value).toLocaleString("en-IN")}`}/-
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="card rounded-0 border-0">
              <div className="card-header border-0 rounded-0 d-flex align-items-center">
                <h6>Payment Details</h6>
              </div>
              <div className="card-body">
                <div className="row mt-2">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <p className="fs-13 mb-0">Payment Type</p>
                      <span className="text-gray-900 fs-13 fw-medium">
                        {order?.payment?.method}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <p className="fs-13 mb-0">Payment Status</p>
                      <span className="text-gray-900 fs-13 fw-medium">
                        {order?.payment?.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <p className="fs-13 mb-0">Payment Date & Time</p>
                      <span className="text-gray-900 fs-13 fw-medium">
                        {formatDate(order?.payment?.paidAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {order?.payment?.details?.gatewayOrderId && (
                  <div className="row mt-2">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <p className="fs-13 mb-0">Gateway Order Id</p>
                        <span className="text-gray-900 fs-13 fw-medium">
                          {order?.payment?.details?.gatewayOrderId}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <p className="fs-13 mb-0">Payment Id</p>
                        <span className="text-gray-900 fs-13 fw-medium">
                          {order?.payment?.details?.id}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <p className="fs-13 mb-0">Payment Method</p>
                        <span className="text-gray-900 fs-13 fw-medium">
                          {order?.payment?.details?.method}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {order?.payment?.details?.razorpayPaymentId && (
                  <div className="row mt-2">
                    <div className="col-md-4">
                      <div className="mb-3">
                        <p className="fs-13 mb-0">Razorpay Order ID</p>
                        <span className="text-gray-900 fs-13 fw-medium">
                          {order?.payment?.details?.razorpay_order_id}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-3">
                        <p className="fs-13 mb-0">Razorpay Payment ID</p>
                        <span className="text-gray-900 fs-13 fw-medium">
                          {order?.payment?.details?.razorpayPaymentId}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {order?.payment?.details?.razorpaySignature && (
                  <div className="row mt-2">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <p className="fs-13 mb-0">Razorpay Signature</p>
                        <span className="text-gray-900 fs-13 fw-medium">
                          {order?.payment?.details?.razorpaySignature}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        style={{ width: "750px" }}
        id="offcanvasShipment"
        aria-labelledby="offcanvasShipmentLabel"
      >
        {selectedOrder ? (
          <ShipmentDetail item={selectedOrder} />
        ) : (
          <p className="p-3">Loading...</p>
        )}
      </div>

      {showCustomModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-palette-fill me-2 text-primary"></i>
                  Customization Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCustomModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {selectedCustomization ? (
                  <div className="table-responsive">
                    <table className="table table-bordered sm fs-14">
                      <thead className="table-light">
                        <tr>
                          <th>Option / Field</th>
                          <th>Value Selected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Agar customization object form mein hai toh use render karein */}
                        {typeof selectedCustomization === "object" ? (
                          Object.entries(selectedCustomization).map(
                            ([key, value]) => (
                              <tr key={key}>
                                <td className="fw-semibold text-capitalize">
                                  {key.replace(/_/g, " ")}
                                </td>
                                <td>{String(value)}</td>
                              </tr>
                            ),
                          )
                        ) : (
                          <tr>
                            <td colSpan="2">{selectedCustomization}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted text-centermy-3">
                    No details found.
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowCustomModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Detail;
